import React, { useState, useEffect } from 'react';
import {
  Building2, TrendingUp, DollarSign, Percent, Activity,
  BarChart3, PieChart, Calendar, AlertCircle, Loader,
  ChevronDown, ChevronUp, Info, ArrowUpRight, ArrowDownRight,
  Users, FileText, Briefcase, Award, Target, TrendingDown, Zap
} from 'lucide-react';

// Import existing components
import StockNews from './StockNews';
import SectorAnalysis from './SectorAnalysis';
import SocialSentiment from './SocialSentiment';
import AlphaIntelligence from './AlphaIntelligence';
import EarningsCalendar from './EarningsCalendar';
import PageAwareAI from './PageAwareAI';
import CorporateActions from './CorporateActions';
import TechnicalAnalysisTab from './TechnicalAnalysisTab';
import { useMarket } from '../context/MarketContext';

const CompanyAnalysis = ({ symbol: initialSymbol }) => {
  const { selectedMarket } = useMarket();
  const [currentSymbol, setCurrentSymbol] = useState(initialSymbol || '');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [incomeStatements, setIncomeStatements] = useState([]);
  const [balanceSheets, setBalanceSheets] = useState([]);
  const [cashFlows, setCashFlows] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [financialPeriod, setFinancialPeriod] = useState('quarterly');
  
  // Price chart states
  const [priceData, setPriceData] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('daily');
  
  // Currency helper
  const currency = selectedMarket === 'IN' ? '₹' : '$';

  const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

  useEffect(() => {
    if (initialSymbol) {
      setCurrentSymbol(initialSymbol);
    }
  }, [initialSymbol]);

  useEffect(() => {
    if (currentSymbol) {
      setPriceData(null); // Clear stale price data when symbol changes
      fetchCompanyData();
    }
  }, [currentSymbol, selectedMarket]);

  useEffect(() => {
    if (currentSymbol && (activeTab === 'financials')) {
      fetchFinancials();
    }
  }, [financialPeriod, activeTab, selectedMarket]);

  useEffect(() => {
    if (currentSymbol && activeTab === 'chart') {
      fetchPriceData();
    }
  }, [currentSymbol, activeTab, selectedTimeframe, selectedMarket]);

  const fetchPriceData = async () => {
    setPriceLoading(true);
    try {
      const endpoint = selectedMarket === 'IN'
        ? `${API_URL}/india/historical/${currentSymbol}?timeframe=${selectedTimeframe}`
        : `${API_URL}/stocks/${currentSymbol}/prices/${selectedTimeframe}`;
      
      console.log(`📊 Fetching price data from: ${endpoint}`);
      const response = await fetch(endpoint);
      const data = await response.json();
      
      console.log('📊 Price data received:', data);
      setPriceData(data);
    } catch (err) {
      console.error('❌ Error fetching price data:', err);
    } finally {
      setPriceLoading(false);
    }
  };


  const fetchCompanyData = async () => {
    setLoading(true);
    setError(null);

    try {
      let overviewRes, earningsRes;
      
      if (selectedMarket === 'IN') {
        // India market - use India endpoints
        console.log('🇮🇳 Fetching India company data for:', currentSymbol);
        [overviewRes, earningsRes] = await Promise.all([
          fetch(`${API_URL}/india/fundamentals/${currentSymbol}`),
          fetch(`${API_URL}/india/earnings/${currentSymbol}`)
        ]);
      } else {
        // US market - use existing endpoints
        console.log('🇺🇸 Fetching US company data for:', currentSymbol);
        [overviewRes, earningsRes] = await Promise.all([
          fetch(`${API_URL}/company/${currentSymbol}/overview`),
          fetch(`${API_URL}/company/${currentSymbol}/earnings`)
        ]);
      }

      // Check if overview request failed
      if (!overviewRes.ok) {
        const errorData = await overviewRes.json().catch(() => ({}));
        console.error(`❌ Overview API error (${overviewRes.status}):`, errorData);
        
        if (overviewRes.status === 429) {
          setError('Rate limit reached. Please wait a moment and try again.');
        } else if (overviewRes.status === 404) {
          setError(`Company "${currentSymbol}" not found. Try a different symbol.`);
        } else {
          setError(errorData.error || 'Failed to load company data');
        }
        setLoading(false);
        return;
      }

      const overviewData = await overviewRes.json();
      const earningsData = await earningsRes.json().catch(() => ({ success: false }));

      // Handle India data format (may be different)
      if (selectedMarket === 'IN') {
        if (overviewData.success !== false && !overviewData.error) {
          // India API returns data directly or wrapped
          setOverview(overviewData.data || overviewData);
        } else {
          setError(overviewData.error || 'Failed to load India company data');
        }
        if (earningsData.success !== false && !earningsData.error) {
          setEarnings(earningsData.data || earningsData);
        }
      } else {
        // Existing US logic
        if (overviewData.success) {
          setOverview(overviewData.data);
        } else {
          setError(overviewData.error || 'Company data unavailable');
        }
        if (earningsData.success) {
          setEarnings(earningsData.data);
        }
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancials = async () => {
    try {
      let incomeRes, balanceRes, cashFlowRes;
      
      if (selectedMarket === 'IN') {
        // India market - use India endpoints
        console.log('🇮🇳 Fetching India financials for:', currentSymbol);
        [incomeRes, balanceRes, cashFlowRes] = await Promise.all([
          fetch(`${API_URL}/india/company/${currentSymbol}/income-statement?period=${financialPeriod}`),
          fetch(`${API_URL}/india/company/${currentSymbol}/balance-sheet?period=${financialPeriod}`),
          fetch(`${API_URL}/india/company/${currentSymbol}/cash-flow?period=${financialPeriod}`)
        ]);
      } else {
        // US market - use existing endpoints
        console.log('🇺🇸 Fetching US financials for:', currentSymbol);
        [incomeRes, balanceRes, cashFlowRes] = await Promise.all([
          fetch(`${API_URL}/company/${currentSymbol}/income-statement?period=${financialPeriod}`),
          fetch(`${API_URL}/company/${currentSymbol}/balance-sheet?period=${financialPeriod}`),
          fetch(`${API_URL}/company/${currentSymbol}/cash-flow?period=${financialPeriod}`)
        ]);
      }

      const incomeData = await incomeRes.json();
      const balanceData = await balanceRes.json();
      const cashFlowData = await cashFlowRes.json();

      if (incomeData.success) setIncomeStatements(incomeData.data.statements || []);
      if (balanceData.success) setBalanceSheets(balanceData.data.statements || []);
      if (cashFlowData.success) setCashFlows(cashFlowData.data.statements || []);

    } catch (err) {
      console.error('Error fetching financials:', err);
    }
  };

  // ============================================
  // AI CONTEXT BUILDER
  // ============================================
  const buildAIContext = () => {
    return {
      currentStock: overview ? {
        symbol: overview.symbol,
        name: overview.name,
        price: overview.price,
        sector: overview.sector,
        industry: overview.industry,
        fundamentals: {
          peRatio: overview.peRatio,
          marketCap: overview.marketCap,
          roe: overview.returnOnEquity,
          roa: overview.returnOnAssets,
          profitMargin: overview.profitMargin,
          eps: overview.eps
        }
      } : null,
      
      currentTab: activeTab,
      tabData: getTabSpecificData()
    };
  };

  const getTabSpecificData = () => {
    switch(activeTab) {
      case 'overview':
        return {
          tab: 'Overview',
          metrics: overview,
          capabilities: [
            "Explain what P/E ratio means for this stock",
            "Is this stock overvalued?",
            "What does the ROE tell me?",
            "Should I invest in this company?",
            "Compare this to industry averages"
          ]
        };
        
      case 'financials':
        return {
          tab: 'Financials',
          incomeStatements: incomeStatements.slice(0, 8),
          cashFlows: cashFlows.slice(0, 8),
          latestRevenue: incomeStatements[0]?.revenue,
          latestMargins: {
            gross: incomeStatements[0]?.grossProfitMargin,
            operating: incomeStatements[0]?.operatingMargin,
            net: incomeStatements[0]?.netProfitMargin
          },
          latestCashFlow: {
            operating: cashFlows[0]?.operatingCashFlow,
            investing: cashFlows[0]?.investingCashFlow,
            financing: cashFlows[0]?.financingCashFlow
          },
          capabilities: [
            "Is the revenue growing?",
            "Why is financing cash flow negative?",
            "What's a healthy profit margin?",
            "Explain gross vs net margin",
            "Is the cash flow healthy?"
          ]
        };
        
      case 'earnings':
        return {
          tab: 'Earnings',
          earningsHistory: earnings,
          capabilities: [
            "What does the earnings pattern mean?",
            "Should I buy before earnings?",
            "What's the earnings trend?",
            "Is this company consistent?"
          ]
        };
        
      case 'health':
        return {
          tab: 'Health Score',
          healthScore: calculateHealthScore(overview),
          profitabilityScore: calculateProfitabilityScore(overview),
          valuationScore: calculateValuationScore(overview),
          growthScore: calculateGrowthScore(overview),
          capabilities: [
            "What does my health score mean?",
            "Why is valuation score at this level?",
            "Is profitability good?",
            "Should I be concerned about growth?",
            "How does this compare to industry?"
          ]
        };
        
      default:
        return { tab: 'Unknown', capabilities: [] };
    }
  };

  if (loading && !overview) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4rem'
      }}>
        <Loader size={48} color="#8b5cf6" className="spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '0.75rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 1rem' }} />
        <p style={{ color: '#f87171', margin: 0 }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>

      {/* ── AI Top Picks Strip ───────────────────────────────────────── */}
      <AiPicksStrip onSelectStock={setCurrentSymbol} />

      {/* ── Compact Sector Browser ───────────────────────────────────── */}
      <CompactSectorBrowser onSelectStock={setCurrentSymbol} />

      {/* ── Inline Search Bar above company analysis ──────────────── */}
      <InlineSearchBar onSelectStock={setCurrentSymbol} currentSymbol={currentSymbol} />

      {/* Header - Only show when stock is selected */}
      {currentSymbol && (
        <div style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          borderRadius: '0.75rem',
          padding: 'clamp(1rem, 3vw, 2rem)',
          marginBottom: '2rem'
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <h2 style={{
                fontSize: 'clamp(1.25rem, 4vw, 2rem)',
                fontWeight: 'bold',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Building2 size={32} color="white" />
                {overview?.name || currentSymbol}
              </h2>
              {/* Change Stock Button */}
              <button
                onClick={() => {
                  setCurrentSymbol('');
                  setOverview(null);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                🔄 Change Stock
              </button>
            </div>
            <div style={{ color: '#e0e7ff', fontSize: '0.875rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <span>{overview?.symbol}</span>
              <span>•</span>
              <span>{overview?.exchange}</span>
              <span>•</span>
              <span>{overview?.sector}</span>
              <span>•</span>
              <span>{overview?.industry}</span>
            </div>
          </div>

          {overview && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: '#e0e7ff', marginBottom: '0.25rem' }}>
                Market Cap
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}>
                {currency}{formatLargeNumber(overview.marketCap)}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Tab Navigation - Only show when stock is selected */}
      {currentSymbol && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          borderBottom: '2px solid #334155',
          paddingBottom: '0.5rem',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin'
        }}>
        <TabButton
          active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
          icon={<Info size={18} />}
          label="Overview"
        />
        <TabButton
          active={activeTab === 'financials'}
          onClick={() => {
            setActiveTab('financials');
            if (incomeStatements.length === 0) fetchFinancials();
          }}
          icon={<BarChart3 size={18} />}
          label="Financials"
        />
        <TabButton
          active={activeTab === 'earnings'}
          onClick={() => setActiveTab('earnings')}
          icon={<TrendingUp size={18} />}
          label="Earnings"
        />
        <TabButton
          active={activeTab === 'technical'}
          onClick={() => setActiveTab('technical')}
          icon={<Zap size={18} />}
          label="Technical"
        />
        <TabButton
          active={activeTab === 'health'}
          onClick={() => setActiveTab('health')}
          icon={<Activity size={18} />}
          label="Health Score"
        />
        <TabButton
          active={activeTab === 'smartmoney'}
          onClick={() => {
            setActiveTab('smartmoney');
            fetchPriceData();
          }}
          icon={<span style={{ fontSize: '1rem' }}>🏦</span>}
          label="Smart Money"
        />
      </div>
      )}

      {/* Tab Content - Only show when stock is selected */}
      {currentSymbol && (
        <>
          {activeTab === 'overview' && overview && (
            <OverviewTab overview={overview} symbol={currentSymbol} selectedMarket={selectedMarket} />
          )}

          {activeTab === 'financials' && (
            <FinancialsTab
              incomeStatements={incomeStatements}
              balanceSheets={balanceSheets}
              cashFlows={cashFlows}
              period={financialPeriod}
              setPeriod={setFinancialPeriod}
              currency={currency}
            />
          )}

          {activeTab === 'earnings' && (
            <EarningsCalendar symbol={currentSymbol} />
          )}

          {activeTab === 'technical' && (
            <TechnicalAnalysisTab symbol={currentSymbol} currentPrice={overview?.price} />
          )}

          {activeTab === 'health' && overview && (
            <HealthScoreTab overview={overview} selectedMarket={selectedMarket} />
          )}

          {activeTab === 'smartmoney' && (
            <SmartMoneyTab
              symbol={currentSymbol}
              priceData={priceData}
              priceLoading={priceLoading}
              onFetchPrice={fetchPriceData}
            />
          )}
        </>
      )}

      {/* AI Assistant - Shows when stock is selected */}
      <PageAwareAI 
        pageId="company" 
        pageData={{ 
          stock: overview, 
          activeTab: activeTab,
          financials: { 
            income: incomeStatements, 
            cashFlow: cashFlows,
            balance: balanceSheets
          }, 
          earnings: earnings
        }} 
        symbol={currentSymbol}
      />

    </div>
  );
};

// ==============================================
// OVERVIEW TAB
// ==============================================
const OverviewTab = ({ overview, symbol, selectedMarket }) => {
  const healthScore = calculateHealthScore(overview);
  
  // Currency helper
  const currency = selectedMarket === 'IN' ? '₹' : '$';
  
  const [priceData, setPriceData] = React.useState(null);
  const [priceLoading, setPriceLoading] = React.useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = React.useState('daily');
  
  const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

  // Fetch price data when symbol, timeframe, or market changes
  React.useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;
    
    const fetchPriceData = async () => {
      if (!symbol) return;
      
      setPriceLoading(true);
      setPriceData(null); // Clear old data while loading
      
      const attemptFetch = async () => {
        try {
          const endpoint = selectedMarket === 'IN'
            ? `${API_URL}/india/historical/${symbol}?timeframe=${selectedTimeframe}`
            : `${API_URL}/stocks/${symbol}/prices/${selectedTimeframe}`;
          
          console.log(`📊 Fetching price data for ${symbol} from: ${endpoint}`);
          const response = await fetch(endpoint);
          
          if (!response.ok) {
            // If 404 and we have retries left, wait and retry
            if (response.status === 404 && retryCount < maxRetries) {
              retryCount++;
              console.log(`⏳ Retry ${retryCount}/${maxRetries} for ${symbol} price data...`);
              await new Promise(resolve => setTimeout(resolve, 500));
              return attemptFetch();
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          console.log(`✅ Price data received for ${symbol}:`, data);
          
          if (isMounted) {
            setPriceData(data);
          }
        } catch (error) {
          console.error(`❌ Error fetching price data for ${symbol}:`, error);
          if (isMounted) {
            setPriceData(null);
          }
        } finally {
          if (isMounted) {
            setPriceLoading(false);
          }
        }
      };
      
      await attemptFetch();
    };

    fetchPriceData();
    
    return () => {
      isMounted = false;
    };
  }, [symbol, selectedTimeframe, selectedMarket, API_URL]);

  return (
    <div>
      {/* Price Chart Section */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          {['daily', 'weekly', 'monthly'].map((tf) => (
            <button
              key={tf}
              onClick={() => setSelectedTimeframe(tf)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: selectedTimeframe === tf ? '2px solid #60a5fa' : '1px solid #475569',
                background: selectedTimeframe === tf ? 'rgba(96, 165, 250, 0.2)' : '#334155',
                color: selectedTimeframe === tf ? '#60a5fa' : '#cbd5e1',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: selectedTimeframe === tf ? '600' : '400',
                textTransform: 'capitalize',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedTimeframe !== tf) {
                  e.currentTarget.style.borderColor = '#60a5fa';
                  e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTimeframe !== tf) {
                  e.currentTarget.style.borderColor = '#475569';
                  e.currentTarget.style.background = '#334155';
                }
              }}
            >
              {tf}
            </button>
          ))}
        </div>

        {priceLoading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <Loader size={32} className="spin" style={{ color: '#60a5fa' }} />
            <p style={{ color: '#94a3b8', marginTop: '1rem' }}>Loading price data...</p>
          </div>
        ) : priceData ? (
          <EnhancedPriceChart 
            data={priceData.data || priceData.prices || priceData} 
            performance={priceData.performance}
            symbol={symbol}
            timeframe={selectedTimeframe}
            currency={currency}
          />
        ) : (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
            No price data available
          </div>
        )}
      </div>

      {/* Key Metrics Grid - Mobile Optimized */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.75rem',
        marginBottom: '2rem'
      }}>
        <CompactMetricCard
          label="P/E Ratio"
          value={overview.peRatio?.toFixed(2) || 'N/A'}
          icon={<Percent size={16} color="#60a5fa" />}
          color="#60a5fa"
        />
        <CompactMetricCard
          label="EPS"
          value={`${currency}${overview.eps?.toFixed(2) || 'N/A'}`}
          icon={<DollarSign size={16} color="#10b981" />}
          color="#10b981"
        />
        <CompactMetricCard
          label="Dividend"
          value={overview.dividendYield ? `${(overview.dividendYield * 100).toFixed(2)}%` : 'N/A'}
          icon={<TrendingUp size={16} color="#8b5cf6" />}
          color="#8b5cf6"
        />
        <CompactMetricCard
          label="52W High"
          value={`${currency}${overview.week52High?.toFixed(2) || 'N/A'}`}
          icon={<ArrowUpRight size={16} color="#10b981" />}
          color="#10b981"
        />
        <CompactMetricCard
          label="52W Low"
          value={`${currency}${overview.week52Low?.toFixed(2) || 'N/A'}`}
          icon={<ArrowDownRight size={16} color="#ef4444" />}
          color="#ef4444"
        />
        <CompactMetricCard
          label="Beta"
          value={overview.beta?.toFixed(2) || 'N/A'}
          icon={<Activity size={16} color="#f59e0b" />}
          color="#f59e0b"
        />
      </div>

      {/* Profitability Metrics */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <TrendingUp size={20} color="#10b981" />
          Profitability Metrics
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <SimpleMetric label="Profit Margin" value={`${(overview.profitMargin * 100)?.toFixed(2) || 0}%`} />
          <SimpleMetric label="Operating Margin" value={`${(overview.operatingMargin * 100)?.toFixed(2) || 0}%`} />
          <SimpleMetric label="Return on Equity" value={`${(overview.returnOnEquity * 100)?.toFixed(2) || 0}%`} />
          <SimpleMetric label="Return on Assets" value={`${(overview.returnOnAssets * 100)?.toFixed(2) || 0}%`} />
        </div>
      </div>

      {/* Valuation Metrics */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <DollarSign size={20} color="#60a5fa" />
          Valuation Ratios
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <SimpleMetric label="P/E Ratio" value={overview.peRatio?.toFixed(2) || 'N/A'} />
          <SimpleMetric label="PEG Ratio" value={overview.pegRatio?.toFixed(2) || 'N/A'} />
          <SimpleMetric label="Price to Book" value={overview.priceToBook?.toFixed(2) || 'N/A'} />
          <SimpleMetric label="Price to Sales" value={overview.priceToSales?.toFixed(2) || 'N/A'} />
          <SimpleMetric label="EV to Revenue" value={overview.evToRevenue?.toFixed(2) || 'N/A'} />
          <SimpleMetric label="EV to EBITDA" value={overview.evToEbitda?.toFixed(2) || 'N/A'} />
        </div>
      </div>

      {/* Corporate Actions - Dividends, Splits, Shares Outstanding */}
      <CorporateActions symbol={symbol} />

      {/* Company Description */}
      {overview.description && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={20} color="#94a3b8" />
            About {overview.name}
          </h3>
          <p style={{ color: '#cbd5e1', lineHeight: '1.6', fontSize: '0.875rem' }}>
            {overview.description}
          </p>
        </div>
      )}

      {/* Revenue & Valuation Breakdown */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ 
          fontSize: '1.125rem', 
          marginBottom: '1.5rem', 
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <PieChart size={20} color="#8b5cf6" />
          Company Metrics Breakdown
        </h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          <div>
            <h4 style={{ 
              fontSize: '0.875rem', 
              color: '#cbd5e1', 
              marginBottom: '1rem',
              fontWeight: '600'
            }}>
              Market Valuation Distribution
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {[
                { label: 'Market Cap', value: overview.marketCap, color: '#10b981' },
                { label: 'Enterprise Value', value: overview.enterpriseValue || overview.marketCap * 1.1, color: '#60a5fa' },
                { label: 'Revenue (TTM)', value: overview.revenue, color: '#8b5cf6' }
              ].map((metric, idx) => {
                const marketCap = overview.marketCap || 0;
                const enterpriseValue = overview.enterpriseValue || marketCap * 1.1 || 0;
                const revenue = overview.revenue || 0;
                
                const total = marketCap + enterpriseValue + revenue;
                const percentage = (total > 0 && metric.value > 0)
                  ? ((metric.value / total) * 100).toFixed(1)
                  : '0.0';
                
                if (metric.value === 0) {
                  return (
                    <div key={idx}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            width: '12px',
                            height: '12px',
                            background: metric.color,
                            borderRadius: '2px',
                            opacity: 0.3
                          }} />
                          <span style={{ 
                            fontSize: '0.75rem', 
                            color: '#94a3b8',
                            fontWeight: '600'
                          }}>
                            {metric.label}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '0.75rem',
                          color: '#94a3b8',
                          fontWeight: '700'
                        }}>
                          N/A
                        </span>
                      </div>
                      <div style={{
                        height: '24px',
                        background: 'rgba(15, 23, 42, 0.6)',
                        borderRadius: '0.5rem',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '0.75rem'
                      }}>
                        <span style={{
                          fontSize: '0.7rem',
                          color: '#94a3b8',
                          fontWeight: '600'
                        }}>
                          Data unavailable
                        </span>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div key={idx}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          background: metric.color,
                          borderRadius: '2px'
                        }} />
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#cbd5e1',
                          fontWeight: '600'
                        }}>
                          {metric.label}
                        </span>
                      </div>
                      <span style={{
                        fontSize: '0.75rem',
                        color: metric.color,
                        fontWeight: '700'
                      }}>
                        {percentage}%
                      </span>
                    </div>
                    <div style={{
                      height: '24px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      borderRadius: '0.5rem',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${percentage}%`,
                        background: `linear-gradient(to right, ${metric.color}, ${metric.color}cc)`,
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '0.75rem',
                        transition: 'width 0.5s ease'
                      }}>
                        <span style={{
                          fontSize: '0.7rem',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          {currency}{formatLargeNumber(metric.value)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h4 style={{ 
              fontSize: '0.875rem', 
              color: '#cbd5e1', 
              marginBottom: '1rem',
              fontWeight: '600'
            }}>
              Key Financial Ratios
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.75rem'
            }}>
              {[
                { label: 'P/E Ratio', value: overview.peRatio?.toFixed(2) || 'N/A', color: '#60a5fa' },
                { label: 'P/B Ratio', value: overview.priceToBook?.toFixed(2) || 'N/A', color: '#10b981' },
                { label: 'Debt/Equity', value: overview.debtToEquity?.toFixed(2) || 'N/A', color: '#f59e0b' },
                { label: 'Current Ratio', value: overview.currentRatio?.toFixed(2) || 'N/A', color: '#8b5cf6' }
              ].map((ratio, idx) => (
                <div key={idx} style={{
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: `1px solid ${ratio.color}44`,
                  borderRadius: '0.5rem',
                  padding: '0.75rem'
                }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#94a3b8',
                    marginBottom: '0.25rem',
                    fontWeight: '600'
                  }}>
                    {ratio.label}
                  </div>
                  <div style={{
                    fontSize: '1.25rem',
                    color: ratio.color,
                    fontWeight: '700'
                  }}>
                    {ratio.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Market Context Section */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          marginBottom: '1.5rem', 
          fontWeight: '600',
          color: '#60a5fa'
        }}>
          📊 Market Context & Analysis
        </h3>
        
        <SectorAnalysisSection symbol={symbol} />
        
        <div style={{ marginTop: '2rem' }}>
          <AlphaIntelligence symbol={symbol} />
        </div>
        
        <div style={{ marginTop: '2rem' }}>
          <SocialSentimentSection symbol={symbol} />
        </div>
      </div>
    </div>
  );
};

// ==============================================
// FINANCIALS TAB
// ==============================================
const FinancialsTab = ({ incomeStatements, balanceSheets, cashFlows, period, setPeriod, currency = '$' }) => {
  if (!incomeStatements || incomeStatements.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader size={32} className="spin" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={() => setPeriod('quarterly')}
          style={{
            padding: '0.5rem 1rem',
            background: period === 'quarterly' ? '#8b5cf6' : '#334155',
            border: 'none',
            borderRadius: '0.375rem',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}
        >
          Quarterly
        </button>
        <button
          onClick={() => setPeriod('annual')}
          style={{
            padding: '0.5rem 1rem',
            background: period === 'annual' ? '#8b5cf6' : '#334155',
            border: 'none',
            borderRadius: '0.375rem',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}
        >
          Annual
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem'
        }}>
          <h3 style={{ 
            fontSize: '1.125rem', 
            marginBottom: '1rem', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <TrendingUp size={20} color="#10b981" />
            Revenue Trend
          </h3>
          <SimpleBarChart
            data={incomeStatements.slice(0, 8).reverse()}
            dataKey="revenue"
            label="Revenue"
            color="#10b981"
            currency={currency}
          />
        </div>

        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem'
        }}>
          <h3 style={{ 
            fontSize: '1.125rem', 
            marginBottom: '1rem', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <DollarSign size={20} color="#60a5fa" />
            Net Income Trend
          </h3>
          <SimpleBarChart
            data={incomeStatements.slice(0, 8).reverse()}
            dataKey="netIncome"
            label="Net Income"
            color="#60a5fa"
            currency={currency}
          />
        </div>
      </div>

      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', fontWeight: '600' }}>
          Profit Margins
        </h3>
        <SimpleLineChart
          data={incomeStatements.slice(0, 8).reverse()}
          lines={[
            { dataKey: 'grossProfitMargin', label: 'Gross', color: '#10b981' },
            { dataKey: 'operatingMargin', label: 'Operating', color: '#60a5fa' },
            { dataKey: 'netProfitMargin', label: 'Net', color: '#8b5cf6' }
          ]}
          currency={currency}
        />
      </div>

      {cashFlows && cashFlows.length > 0 && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', fontWeight: '600' }}>
            Cash Flow Analysis
          </h3>
          <SimpleLineChart
            data={cashFlows.slice(0, 8).reverse()}
            lines={[
              { dataKey: 'operatingCashFlow', label: 'Operating', color: '#10b981' },
              { dataKey: 'investingCashFlow', label: 'Investing', color: '#ef4444' },
              { dataKey: 'financingCashFlow', label: 'Financing', color: '#60a5fa' }
            ]}
            currency={currency}
          />
        </div>
      )}
    </div>
  );
};

// ==============================================
// HEALTH SCORE TAB
// ==============================================
const HealthScoreTab = ({ overview, selectedMarket }) => {
  const score = calculateHealthScore(overview);
  const scoreColor = score >= 70 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const currency = selectedMarket === 'IN' ? '₹' : '$';

  return (
    <div>
      <div style={{
        background: `linear-gradient(135deg, ${scoreColor}20, ${scoreColor}10)`,
        border: `2px solid ${scoreColor}40`,
        borderRadius: '0.75rem',
        padding: '2rem',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
          Overall Financial Health Score
        </div>
        <div style={{ fontSize: '4rem', fontWeight: 'bold', color: scoreColor, marginBottom: '0.5rem' }}>
          {score}
        </div>
        <div style={{ fontSize: '1rem', color: '#cbd5e1' }}>
          {score >= 70 ? '🟢 Excellent' : score >= 50 ? '🟡 Good' : '🔴 Needs Improvement'}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem'
      }}>
        <ScoreCard
          label="Profitability"
          score={calculateProfitabilityScore(overview)}
          metrics={[
            { label: 'Profit Margin', value: `${(overview.profitMargin * 100)?.toFixed(2) || 0}%` },
            { label: 'ROE', value: `${(overview.returnOnEquity * 100)?.toFixed(2) || 0}%` },
            { label: 'ROA', value: `${(overview.returnOnAssets * 100)?.toFixed(2) || 0}%` }
          ]}
        />
        <ScoreCard
          label="Valuation"
          score={calculateValuationScore(overview)}
          metrics={[
            { label: 'P/E Ratio', value: overview.peRatio?.toFixed(2) || 'N/A' },
            { label: 'PEG Ratio', value: overview.pegRatio?.toFixed(2) || 'N/A' },
            { label: 'P/B Ratio', value: overview.priceToBook?.toFixed(2) || 'N/A' }
          ]}
        />
        <ScoreCard
          label="Growth"
          score={calculateGrowthScore(overview)}
          metrics={[
            { label: 'Revenue/Share', value: `${currency}${overview.revenuePerShare?.toFixed(2) || 'N/A'}` },
            { label: 'EPS', value: `${currency}${overview.eps?.toFixed(2) || 'N/A'}` },
            { label: 'Operating Margin', value: `${(overview.operatingMargin * 100)?.toFixed(2) || 0}%` }
          ]}
        />
      </div>
    </div>
  );
};

// ==============================================
// HELPER COMPONENTS
// ==============================================
const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    style={{
      padding: '0.75rem 1.5rem',
      background: active ? '#8b5cf6' : 'transparent',
      border: 'none',
      borderRadius: '0.5rem 0.5rem 0 0',
      color: active ? 'white' : '#94a3b8',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap'
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.background = 'transparent';
    }}
  >
    {icon}
    {label}
  </button>
);

const SimpleMetric = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#e2e8f0' }}>
      {value}
    </div>
  </div>
);

const ScoreCard = ({ label, score, metrics }) => {
  const scoreColor = score >= 70 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.5)',
      border: '1px solid #334155',
      borderRadius: '0.75rem',
      padding: '1.5rem'
    }}>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
          {label}
        </div>
        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: scoreColor }}>
          {score}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {metrics.map((metric, idx) => (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
            <span style={{ color: '#94a3b8' }}>{metric.label}:</span>
            <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{metric.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── AI Picks Strip ────────────────────────────────────────────────────────
const AiPicksStrip = ({ onSelectStock }) => {
  const [picks, setPicks] = React.useState([]);
  const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

  React.useEffect(() => {
    fetch(`${API_URL}/top-winners`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.picks?.length) setPicks(data.picks.slice(0, 6)); })
      .catch(() => {});
  }, []);

  if (!picks.length) return null;

  return (
    <div style={{
      background: 'rgba(15,23,42,0.8)',
      border: '1px solid rgba(99,102,241,0.25)',
      borderRadius: '10px',
      padding: '0.6rem 0.9rem',
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      overflowX: 'auto',
      scrollbarWidth: 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
        <span style={{ fontSize: '0.7rem', color: '#818cf8', fontWeight: 700, letterSpacing: '0.06em' }}>🏆 AI PICKS</span>
      </div>
      <div style={{ width: '1px', height: 20, background: '#1e293b', flexShrink: 0 }} />
      {picks.map((p, i) => (
        <button
          key={p.symbol}
          onClick={() => onSelectStock(p.symbol)}
          style={{
            flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: '0.35rem',
            background: 'rgba(99,102,241,0.08)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '6px',
            padding: '0.3rem 0.55rem',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'}
        >
          <span style={{ fontSize: '0.65rem', color: '#475569', fontWeight: 700 }}>#{i+1}</span>
          <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#f1f5f9' }}>{p.symbol}</span>
          {p.upsidePct && (
            <span style={{
              fontSize: '0.65rem', fontWeight: 700,
              color: p.upsidePct.startsWith('+') ? '#34d399' : '#f87171',
            }}>{p.upsidePct}</span>
          )}
          <span style={{
            fontSize: '0.6rem', fontWeight: 700, color: '#818cf8',
            background: 'rgba(99,102,241,0.15)', borderRadius: '4px', padding: '0.05rem 0.25rem',
          }}>{p.score}</span>
        </button>
      ))}
      <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
        <span style={{ fontSize: '0.62rem', color: '#334155' }}>Not investment advice</span>
      </div>
    </div>
  );
};

// ── Compact Sector Browser ─────────────────────────────────────────────────
const SECTORS = [
  { name: 'Tech', emoji: '💻', color: '#60a5fa', stocks: ['AAPL','MSFT','GOOGL','AMZN','META','NVDA','TSLA','NFLX','AMD','INTC','ADBE','CRM'] },
  { name: 'Finance', emoji: '🏦', color: '#10b981', stocks: ['JPM','BAC','GS','WFC','MS','V','MA','C','BLK','SCHW','AXP','USB'] },
  { name: 'Health', emoji: '⚕️', color: '#f472b6', stocks: ['JNJ','UNH','PFE','ABBV','TMO','MRK','LLY','CVS','ABT','DHR','BMY','AMGN'] },
  { name: 'Consumer', emoji: '🛒', color: '#fbbf24', stocks: ['WMT','HD','NKE','SBUX','MCD','DIS','COST','PG','KO','PEP','TGT','LOW'] },
  { name: 'Energy', emoji: '⚡', color: '#a78bfa', stocks: ['XOM','CVX','COP','SLB','EOG','PSX','VLO','OXY','MPC','PXD','KMI','WMB'] },
  { name: 'Industry', emoji: '🏭', color: '#fb923c', stocks: ['BA','CAT','HON','UPS','GE','RTX','LMT','DE','MMM','UNP','FDX','NSC'] },
];

const CompactSectorBrowser = ({ onSelectStock }) => {
  const [activeSector, setActiveSector] = React.useState(null);

  return (
    <div style={{
      background: 'rgba(15,23,42,0.6)',
      border: '1px solid #1e293b',
      borderRadius: '10px',
      padding: '0.65rem 0.9rem',
      marginBottom: '0.75rem',
    }}>
      {/* Sector tabs row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 600, marginRight: '0.2rem', flexShrink: 0 }}>Browse:</span>
        {SECTORS.map(s => (
          <button
            key={s.name}
            onClick={() => setActiveSector(activeSector === s.name ? null : s.name)}
            style={{
              padding: '0.25rem 0.65rem',
              borderRadius: '20px',
              border: `1px solid ${activeSector === s.name ? s.color : s.color + '33'}`,
              background: activeSector === s.name ? s.color + '20' : 'transparent',
              color: activeSector === s.name ? s.color : '#64748b',
              fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {s.emoji} {s.name}
          </button>
        ))}
      </div>

      {/* Stock pills - only show when sector is selected */}
      {activeSector && (() => {
        const sector = SECTORS.find(s => s.name === activeSector);
        return (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '0.35rem',
            marginTop: '0.6rem', paddingTop: '0.6rem',
            borderTop: `1px solid ${sector.color}22`,
          }}>
            {sector.stocks.map(stock => (
              <button
                key={stock}
                onClick={() => { onSelectStock(stock); setActiveSector(null); }}
                style={{
                  padding: '0.25rem 0.6rem',
                  background: sector.color + '15',
                  border: `1px solid ${sector.color}33`,
                  borderRadius: '5px',
                  color: sector.color,
                  fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = sector.color + '30'; e.currentTarget.style.borderColor = sector.color; }}
                onMouseLeave={e => { e.currentTarget.style.background = sector.color + '15'; e.currentTarget.style.borderColor = sector.color + '33'; }}
              >
                {stock}
              </button>
            ))}
          </div>
        );
      })()}
    </div>
  );
};

// ── Inline Search Bar ──────────────────────────────────────────────────────
const InlineSearchBar = ({ onSelectStock, currentSymbol }) => {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [showDrop, setShowDrop] = React.useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

  React.useEffect(() => {
    if (!query || query.length < 1) { setResults([]); setShowDrop(false); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}&limit=6`);
        const d = await r.json();
        const list = Array.isArray(d) ? d : (d.results || d.data || []);
        setResults(list.slice(0, 6));
        setShowDrop(list.length > 0);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const select = (sym) => { onSelectStock(sym); setQuery(''); setResults([]); setShowDrop(false); };

  return (
    <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center',
          background: 'rgba(15,23,42,0.8)', border: '1px solid #334155',
          borderRadius: '8px', padding: '0.5rem 0.85rem', gap: '0.5rem',
        }}>
          <span style={{ fontSize: '0.9rem' }}>🔍</span>
          <input
            type="text" value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && query.trim()) select(query.trim().toUpperCase()); }}
            onFocus={() => results.length > 0 && setShowDrop(true)}
            onBlur={() => setTimeout(() => setShowDrop(false), 150)}
            placeholder={currentSymbol ? `Analyzing ${currentSymbol} — search for another stock...` : 'Search any stock symbol, e.g. AAPL, NVDA, TSLA...'}
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#f1f5f9', fontSize: '0.85rem' }}
          />
          {query && <button type="button" onClick={() => { setQuery(''); setResults([]); }}
            style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '1rem' }}>×</button>}
        </div>
        <button onClick={() => query.trim() && select(query.trim().toUpperCase())} style={{
          padding: '0.5rem 1.1rem', background: 'rgba(99,102,241,0.15)',
          border: '1px solid rgba(99,102,241,0.35)', borderRadius: '8px',
          color: '#818cf8', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
        }}>Analyse →</button>
      </div>
      {showDrop && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: '6rem',
          background: '#0f172a', border: '1px solid #334155', borderRadius: '8px',
          marginTop: '4px', zIndex: 999, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          {results.map((r, i) => {
            const sym = r.symbol || r.ticker || r['1. symbol'] || r;
            const name = r.name || r.description || r['2. name'] || '';
            const type = r.type || r.assetType || r['3. type'] || '';
            return (
              <button key={i} onMouseDown={() => select(sym)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.65rem 1rem', background: 'transparent', border: 'none',
                borderBottom: i < results.length - 1 ? '1px solid #1e293b' : 'none',
                cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#818cf8', minWidth: 50 }}>{sym}</span>
                <span style={{ fontSize: '0.78rem', color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                {type && <span style={{ fontSize: '0.65rem', color: '#475569' }}>{type}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SectorDropdown = ({ sector, onSelectStock }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.6)',
      border: `1px solid ${sector.color}33`,
      borderRadius: '0.5rem',
      overflow: 'hidden'
    }}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: '1rem',
          background: 'transparent',
          border: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          color: sector.color,
          fontSize: '0.875rem',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}
      >
        <span>{sector.name} ({sector.stocks.length})</span>
        <span style={{ 
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
          fontSize: '1.25rem'
        }}>
          ▼
        </span>
      </button>

      {isExpanded && (
        <div style={{
          padding: '0.5rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.5rem',
          borderTop: `1px solid ${sector.color}22`
        }}>
          {sector.stocks.map(stock => (
            <button
              key={stock}
              onClick={() => onSelectStock(stock)}
              style={{
                padding: '0.75rem 0.5rem',
                background: `${sector.color}15`,
                border: `1px solid ${sector.color}33`,
                borderRadius: '0.375rem',
                color: sector.color,
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${sector.color}25`;
                e.currentTarget.style.borderColor = sector.color;
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `${sector.color}15`;
                e.currentTarget.style.borderColor = `${sector.color}33`;
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {stock}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CompactMetricCard = ({ label, value, icon, color }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.5rem',
    padding: '0.875rem'
  }}>
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.375rem', 
      marginBottom: '0.5rem' 
    }}>
      {icon}
      <span style={{ 
        fontSize: '0.7rem',
        color: '#94a3b8', 
        textTransform: 'uppercase',
        fontWeight: '600'
      }}>
        {label}
      </span>
    </div>
    <div style={{ 
      fontSize: '1.25rem',
      fontWeight: 'bold', 
      color: color || '#e2e8f0'
    }}>
      {value}
    </div>
  </div>
);

// ==============================================
// CHARTS
// ==============================================
const SimpleBarChart = ({ data, dataKey, label, color, currency = '$' }) => {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));

  return (
    <div style={{ 
      width: '100%',
      height: '280px',
      position: 'relative',
      overflowX: 'auto',
      background: 'rgba(15, 23, 42, 0.4)',
      borderRadius: '0.5rem',
      padding: '3rem 0.5rem 3rem 0.5rem'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '1.25rem',
        height: '100%',
        minWidth: 'fit-content',
        paddingLeft: '1rem',
        paddingRight: '1rem'
      }}>
        {data.map((item, idx) => {
          const height = ((item[dataKey] || 0) / maxValue) * 100;
          
          return (
            <div
              key={idx}
              style={{
                flex: 1,
                minWidth: '50px',
                maxWidth: '60px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              <div style={{ 
                position: 'absolute',
                top: '-2.5rem',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '0.7rem',
                color: '#cbd5e1',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)'
              }}>
                {currency}{formatLargeNumber(item[dataKey])}
              </div>
              
              <div
                style={{
                  width: '100%',
                  height: `${Math.max(height, 5)}%`,
                  background: `linear-gradient(180deg, ${color}dd 0%, ${color} 100%)`,
                  borderRadius: '0.375rem 0.375rem 0 0',
                  boxShadow: `0 -1px 8px ${color}33`,
                  border: 'none',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  opacity: 0.95
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'scaleY(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.95';
                  e.currentTarget.style.transform = 'scaleY(1)';
                }}
                title={`${label}: ${currency}${formatLargeNumber(item[dataKey])}`}
              >
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '20%',
                  right: '20%',
                  height: '25%',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.15), transparent)',
                  borderRadius: '0.25rem'
                }} />
              </div>
              
              <div style={{ 
                position: 'absolute',
                bottom: '-2.5rem',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '0.65rem',
                color: '#94a3b8',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}>
                {item.fiscalDate?.substring(0, 7) || ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SimpleLineChart = ({ data, lines, currency = '$' }) => {
  if (!data || data.length === 0) return null;

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1rem', 
        flexWrap: 'wrap'
      }}>
        {lines.map((line, idx) => (
          <div key={idx} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem'
          }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              background: line.color, 
              borderRadius: '2px' 
            }} />
            <span style={{ 
              fontSize: '0.75rem', 
              color: '#cbd5e1',
              fontWeight: '600'
            }}>
              {line.label}
            </span>
          </div>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #334155' }}>
              <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Period</th>
              {lines.map((line, idx) => (
                <th key={idx} style={{ ...tableHeaderStyle, color: line.color }}>
                  {line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #334155' }}>
                <td style={{ ...tableCellStyle, textAlign: 'left', fontWeight: '600' }}>
                  {item.fiscalDate?.substring(0, 7) || ''}
                </td>
                {lines.map((line, lineIdx) => {
                  const value = item[line.dataKey];
                  
                  const isPercentageField = line.dataKey.toLowerCase().includes('margin') || 
                                           line.dataKey.toLowerCase().includes('ratio') ||
                                           line.dataKey.toLowerCase().includes('yield');
                  
                  let displayValue;
                  if (value === null || value === undefined) {
                    displayValue = 'N/A';
                  } else if (isPercentageField) {
                    displayValue = `${(value * 100).toFixed(2)}%`;
                  } else {
                    displayValue = `${currency}${formatLargeNumber(value)}`;
                  }
                  
                  return (
                    <td 
                      key={lineIdx} 
                      style={{ 
                        ...tableCellStyle,
                        color: line.color,
                        fontWeight: '600'
                      }}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Helper Functions
function formatLargeNumber(num) {
  if (!num) return '0';
  
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  
  let formatted;
  if (absNum >= 1e12) {
    formatted = (absNum / 1e12).toFixed(2) + 'T';
  } else if (absNum >= 1e9) {
    formatted = (absNum / 1e9).toFixed(2) + 'B';
  } else if (absNum >= 1e6) {
    formatted = (absNum / 1e6).toFixed(2) + 'M';
  } else if (absNum >= 1e3) {
    formatted = (absNum / 1e3).toFixed(2) + 'K';
  } else {
    formatted = absNum.toFixed(2);
  }
  
  return isNegative ? '-' + formatted : formatted;
}

function calculateHealthScore(overview) {
  if (!overview) return 0;

  let score = 50;

  if (overview.profitMargin > 0.2) score += 10;
  else if (overview.profitMargin > 0.1) score += 5;

  if (overview.returnOnEquity > 0.15) score += 10;
  else if (overview.returnOnEquity > 0.1) score += 5;

  if (overview.peRatio && overview.peRatio < 25 && overview.peRatio > 10) score += 10;
  if (overview.pegRatio && overview.pegRatio < 2) score += 5;

  if (overview.eps > 0) score += 10;
  if (overview.revenuePerShare > 0) score += 5;

  return Math.min(100, Math.max(0, score));
}

function calculateProfitabilityScore(overview) {
  let score = 50;
  if (overview.profitMargin > 0.2) score += 20;
  else if (overview.profitMargin > 0.1) score += 10;
  if (overview.returnOnEquity > 0.2) score += 20;
  else if (overview.returnOnEquity > 0.1) score += 10;
  if (overview.returnOnAssets > 0.1) score += 10;
  return Math.min(100, score);
}

function calculateValuationScore(overview) {
  let score = 50;
  if (overview.peRatio && overview.peRatio < 20 && overview.peRatio > 10) score += 20;
  if (overview.pegRatio && overview.pegRatio < 1.5) score += 15;
  if (overview.priceToBook && overview.priceToBook < 3) score += 15;
  return Math.min(100, score);
}

function calculateGrowthScore(overview) {
  let score = 50;
  if (overview.eps > 0) score += 20;
  if (overview.revenuePerShare > 0) score += 15;
  if (overview.operatingMargin > 0.15) score += 15;
  return Math.min(100, score);
}

const tableHeaderStyle = {
  padding: '0.75rem',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: '#94a3b8',
  textTransform: 'uppercase',
  textAlign: 'right'
};

const tableCellStyle = {
  padding: '0.75rem',
  fontSize: '0.875rem',
  color: '#cbd5e1',
  textAlign: 'right'
};

// ==============================================
// WRAPPER COMPONENTS FOR OVERVIEW TAB
// ==============================================
const SectorAnalysisSection = ({ symbol }) => {
  return <SectorAnalysis symbol={symbol} />;
};

const SocialSentimentSection = ({ symbol }) => {
  return <SocialSentiment symbol={symbol} />;
};

// ==============================================
// ENHANCED PRICE CHART COMPONENT
// ==============================================
const EnhancedPriceChart = ({ data, performance, symbol, timeframe, currency = '$' }) => {
  const [hoveredPoint, setHoveredPoint] = React.useState(null);
  const chartRef = React.useRef(null);

  const getTimeframeLabel = () => {
    switch(timeframe?.toLowerCase()) {
      case 'daily': return '100-Day Change';
      case 'weekly': return '52-Week Change';
      case 'monthly': return '24-Month Change';
      default: return 'Period Change';
    }
  };

  const getTimeframePeriod = () => {
    switch(timeframe?.toLowerCase()) {
      case 'daily': return '~5 Months';
      case 'weekly': return '1 Year';
      case 'monthly': return '2 Years';
      default: return 'Period';
    }
  };

  if (!data || data.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No price data available</div>;
  }

  const prices = data.map(d => d.close);
  const volumes = data.map(d => d.volume || 0);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const maxVolume = Math.max(...volumes);
  const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
  const priceRange = maxPrice - minPrice;
  const yPadding = priceRange * 0.1;

  const firstPrice = data[0]?.close || 0;
  const lastPrice = data[data.length - 1]?.close || 0;
  const isUptrend = lastPrice >= firstPrice;
  const trendColor = isUptrend ? '#10b981' : '#ef4444';

  const width = 900;
  const height = 500;
  const padding = { top: 30, right: 70, bottom: 80, left: 70 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom - 60;

  const xScale = (index) => (index / (data.length - 1)) * chartWidth;
  const yScale = (price) => chartHeight - ((price - (minPrice - yPadding)) / (maxPrice - minPrice + 2 * yPadding)) * chartHeight;
  const volumeScale = (vol) => (vol / maxVolume) * 50;

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)},${yScale(d.close)}`).join(' ');
  const areaPath = `${linePath} L ${xScale(data.length - 1)},${chartHeight} L 0,${chartHeight} Z`;

  const ma20 = data.map((_, i) => {
    if (i < 19) return null;
    const sum = data.slice(i - 19, i + 1).reduce((a, b) => a + b.close, 0);
    return sum / 20;
  });

  const handleMouseMove = (e) => {
    if (!chartRef.current) return;
    const rect = chartRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - padding.left;
    const index = Math.round((x / chartWidth) * (data.length - 1));
    if (index >= 0 && index < data.length) setHoveredPoint(index);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return timeframe === 'monthly' 
      ? date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div>
      {/* Performance Summary Cards */}
      {performance && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{ background: 'rgba(30, 41, 59, 0.6)', border: `1px solid ${trendColor}33`, borderRadius: '0.75rem', padding: '1rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', background: `radial-gradient(circle at top right, ${trendColor}20, transparent)` }} />
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: '600' }}>{getTimeframeLabel()}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: trendColor, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {parseFloat(performance.change || 0) >= 0 ? '↑' : '↓'}
              {parseFloat(performance.change || 0) >= 0 ? '+' : ''}{parseFloat(performance.change || 0).toFixed(2)}%
            </div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: '600' }}>High ({getTimeframePeriod()})</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{currency}{parseFloat(performance.high || 0).toFixed(2)}</div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: '600' }}>Low ({getTimeframePeriod()})</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{currency}{parseFloat(performance.low || 0).toFixed(2)}</div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: '600' }}>Avg Volume</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>{formatLargeNumber(performance.avgVolume || performance.volume || 0)}</div>
          </div>
        </div>
      )}

      {/* Main Chart Container */}
      <div style={{ background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)', border: '1px solid #334155', borderRadius: '1rem', padding: '1.5rem', position: 'relative' }}>
        {/* Chart Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#e2e8f0', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              📈 {symbol} - {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} Price Chart
            </h3>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
              {data.length} data points • Last updated: {formatDate(data[data.length - 1]?.date)}
            </div>
          </div>
          <div style={{ background: `${trendColor}20`, border: `1px solid ${trendColor}40`, borderRadius: '0.5rem', padding: '0.5rem 1rem' }}>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase' }}>Current</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: trendColor }}>{currency}{lastPrice.toFixed(2)}</div>
          </div>
        </div>

        {/* Hovered Data Display */}
        {hoveredPoint !== null && data[hoveredPoint] && (
          <div style={{
            position: 'absolute', top: '1rem', left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(15, 23, 42, 0.95)', border: '1px solid #475569', borderRadius: '0.75rem',
            padding: '0.75rem 1.5rem', display: 'flex', gap: '2rem', zIndex: 20, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <div><div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>Date</div><div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0' }}>{new Date(data[hoveredPoint].date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</div></div>
            <div><div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>Open</div><div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#94a3b8' }}>{currency}{data[hoveredPoint].open?.toFixed(2) || 'N/A'}</div></div>
            <div><div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>High</div><div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#10b981' }}>{currency}{data[hoveredPoint].high?.toFixed(2) || 'N/A'}</div></div>
            <div><div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>Low</div><div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#ef4444' }}>{currency}{data[hoveredPoint].low?.toFixed(2) || 'N/A'}</div></div>
            <div><div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>Close</div><div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#60a5fa' }}>{currency}{data[hoveredPoint].close?.toFixed(2)}</div></div>
            <div><div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>Volume</div><div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#a78bfa' }}>{formatLargeNumber(data[hoveredPoint].volume || 0)}</div></div>
          </div>
        )}

        {/* SVG Chart */}
        <svg ref={chartRef} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ maxWidth: '100%', cursor: 'crosshair' }} onMouseMove={handleMouseMove} onMouseLeave={() => setHoveredPoint(null)}>
          <defs>
            <linearGradient id={`areaGradient-${symbol}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={trendColor} stopOpacity="0.4" />
              <stop offset="50%" stopColor={trendColor} stopOpacity="0.15" />
              <stop offset="100%" stopColor={trendColor} stopOpacity="0.02" />
            </linearGradient>
            <filter id="glow"><feGaussianBlur stdDeviation="2" result="coloredBlur"/><feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          </defs>

          <g transform={`translate(${padding.left}, ${padding.top})`}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
              const y = chartHeight * ratio;
              const price = maxPrice + yPadding - (maxPrice - minPrice + 2 * yPadding) * ratio;
              return (
                <g key={ratio}>
                  <line x1={0} y1={y} x2={chartWidth} y2={y} stroke="#334155" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
                  <text x={-10} y={y + 4} textAnchor="end" fill="#64748b" fontSize="11" fontWeight="500">{currency}{price.toFixed(2)}</text>
                </g>
              );
            })}

            {/* Average price line */}
            <line x1={0} y1={yScale(avgPrice)} x2={chartWidth} y2={yScale(avgPrice)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="8,4" opacity="0.6" />
            <text x={chartWidth + 5} y={yScale(avgPrice) + 4} fill="#f59e0b" fontSize="10" fontWeight="600">AVG</text>

            {/* 20-day Moving Average */}
            {ma20.filter(v => v !== null).length > 10 && (
              <path
                d={ma20.map((val, i) => val === null ? '' : `${i === ma20.findIndex(v => v !== null) ? 'M' : 'L'} ${xScale(i)},${yScale(val)}`).join(' ')}
                fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="4,2" opacity="0.7"
              />
            )}

            {/* Volume bars */}
            {data.map((d, i) => (
              <rect key={`vol-${i}`} x={xScale(i) - 2} y={chartHeight + 10} width={4} height={volumeScale(d.volume || 0)}
                fill={d.close >= (data[i - 1]?.close || d.open) ? '#10b98150' : '#ef444450'} rx="1" />
            ))}

            {/* Area fill */}
            <path d={areaPath} fill={`url(#areaGradient-${symbol})`} />

            {/* Main price line */}
            <path d={linePath} fill="none" stroke={trendColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow)" />

            {/* Key data points */}
            {data.map((d, i) => {
              const showDot = i === 0 || i === data.length - 1 || i === hoveredPoint;
              if (!showDot) return null;
              return (
                <circle key={`dot-${i}`} cx={xScale(i)} cy={yScale(d.close)} r={i === hoveredPoint ? 8 : 5}
                  fill={i === hoveredPoint ? trendColor : '#0f172a'} stroke={trendColor} strokeWidth={i === hoveredPoint ? 3 : 2}
                  style={{ transition: 'all 0.2s ease' }} />
              );
            })}

            {/* Crosshair on hover */}
            {hoveredPoint !== null && (
              <g>
                <line x1={xScale(hoveredPoint)} y1={0} x2={xScale(hoveredPoint)} y2={chartHeight + 60} stroke="#475569" strokeWidth="1" strokeDasharray="4,4" />
                <line x1={0} y1={yScale(data[hoveredPoint].close)} x2={chartWidth} y2={yScale(data[hoveredPoint].close)} stroke="#475569" strokeWidth="1" strokeDasharray="4,4" />
                <g transform={`translate(${chartWidth + 5}, ${yScale(data[hoveredPoint].close)})`}>
                  <rect x="0" y="-10" width="55" height="20" fill={trendColor} rx="4" />
                  <text x="27" y="4" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">{currency}{data[hoveredPoint].close.toFixed(2)}</text>
                </g>
                <g transform={`translate(${xScale(hoveredPoint)}, ${chartHeight + 70})`}>
                  <rect x="-40" y="-10" width="80" height="20" fill="#334155" rx="4" />
                  <text x="0" y="4" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">{formatDate(data[hoveredPoint].date)}</text>
                </g>
                <circle cx={xScale(hoveredPoint)} cy={yScale(data[hoveredPoint].close)} r="12" fill={`${trendColor}30`} stroke={trendColor} strokeWidth="2" />
              </g>
            )}

            {/* X-axis labels */}
            {data.filter((_, i) => i % Math.ceil(data.length / 6) === 0 || i === data.length - 1).map((d) => {
              const index = data.indexOf(d);
              return <text key={`x-${index}`} x={xScale(index)} y={chartHeight + 75} textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="500">{formatDate(d.date)}</text>;
            })}
          </g>
        </svg>

        {/* Chart Legend */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '20px', height: '3px', background: trendColor, borderRadius: '2px' }} /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Price</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '20px', height: '2px', background: '#8b5cf6', borderRadius: '2px', opacity: 0.7 }} /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>20-Day MA</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '20px', height: '2px', background: '#f59e0b', borderRadius: '2px', opacity: 0.6 }} /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Average</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '12px', height: '12px', background: 'linear-gradient(to top, #10b98150, #10b98120)', borderRadius: '2px' }} /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Volume</span></div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '0.25rem' }}>OPEN</div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0' }}>{currency}{data[0]?.open?.toFixed(2) || firstPrice.toFixed(2)}</div>
        </div>
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '0.25rem' }}>CLOSE</div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: trendColor }}>{currency}{lastPrice.toFixed(2)}</div>
        </div>
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '0.25rem' }}>RANGE</div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#f59e0b' }}>{currency}{priceRange.toFixed(2)}</div>
        </div>
        <div style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '0.25rem' }}>AVG PRICE</div>
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#8b5cf6' }}>{currency}{avgPrice.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
};



// ============================================================
// SMC ANALYSIS ENGINE — Pure frontend OHLC calculations
// ============================================================

function detectMarketStructure(candles) {
  if (!candles || candles.length < 10) return null;
  const c = candles.slice(-50); // last 50 candles
  const highs = c.map(x => x.high || x.close);
  const lows  = c.map(x => x.low  || x.close);

  // Find swing highs and lows (simple: local max/min over 3 candles)
  const swingHighs = [], swingLows = [];
  for (let i = 1; i < c.length - 1; i++) {
    if (highs[i] > highs[i-1] && highs[i] > highs[i+1]) swingHighs.push({ idx: i, price: highs[i], date: c[i].date });
    if (lows[i]  < lows[i-1]  && lows[i]  < lows[i+1])  swingLows.push({  idx: i, price: lows[i],  date: c[i].date });
  }

  if (swingHighs.length < 2 || swingLows.length < 2) return { trend: 'ranging', bos: null, choch: null };

  const lastHH = swingHighs[swingHighs.length - 1];
  const prevHH = swingHighs[swingHighs.length - 2];
  const lastHL = swingLows[swingLows.length - 1];
  const prevHL = swingLows[swingLows.length - 2];
  const lastLL = swingLows[swingLows.length - 1];
  const prevLL = swingLows[swingLows.length - 2];
  const lastLH = swingHighs[swingHighs.length - 1];
  const prevLH = swingHighs[swingHighs.length - 2];

  const currentPrice = c[c.length - 1].close;
  const bullish = lastHH.price > prevHH.price && lastHL.price > prevHL.price;
  const bearish = lastLL.price < prevLL.price && lastLH.price < prevLH.price;
  const trend = bullish ? 'bullish' : bearish ? 'bearish' : 'ranging';

  // Detect recent BOS
  let bos = null;
  if (bullish && lastHH.price > prevHH.price) {
    bos = { direction: 'bullish', price: lastHH.price, date: lastHH.date, label: `BOS ↑ $${lastHH.price.toFixed(2)}` };
  } else if (bearish && lastLL.price < prevLL.price) {
    bos = { direction: 'bearish', price: lastLL.price, date: lastLL.date, label: `BOS ↓ $${lastLL.price.toFixed(2)}` };
  }

  // Detect CHoCH (first break against trend)
  let choch = null;
  if (bullish && lastHL && prevHL && lastHL.price < prevHL.price) {
    choch = { direction: 'bearish', price: lastHL.price, date: lastHL.date, label: `CHoCH ⚠️ $${lastHL.price.toFixed(2)}` };
  } else if (bearish && lastLH && prevLH && lastLH.price > prevLH.price) {
    choch = { direction: 'bullish', price: lastLH.price, date: lastLH.date, label: `CHoCH ⚠️ $${lastLH.price.toFixed(2)}` };
  }

  // Premium/Discount
  const rangeHigh = Math.max(...highs);
  const rangeLow  = Math.min(...lows);
  const equilibrium = (rangeHigh + rangeLow) / 2;
  const zone = currentPrice > equilibrium ? 'premium' : 'discount';
  const zonePercent = ((currentPrice - rangeLow) / (rangeHigh - rangeLow) * 100).toFixed(0);

  return { trend, bos, choch, rangeHigh, rangeLow, equilibrium, zone, zonePercent, swingHighs, swingLows };
}

function detectOrderBlocks(candles) {
  if (!candles || candles.length < 5) return [];
  const c = candles.slice(-60);
  const obs = [];

  for (let i = 1; i < c.length - 3; i++) {
    const candle = c[i];
    // Check next 3 candles for strong displacement
    const nextCandles = c.slice(i + 1, i + 4);
    const avgBody = nextCandles.reduce((s, x) => s + Math.abs(x.close - x.open), 0) / nextCandles.length;
    const displacement = Math.abs(c[i+1].close - c[i].close);
    const isStrong = displacement > avgBody * 1.5;

    if (!isStrong) continue;

    const isBullishOB = candle.close < candle.open && c[i+1].close > c[i+1].open; // bearish then bullish
    const isBearishOB = candle.close > candle.open && c[i+1].close < c[i+1].open; // bullish then bearish

    if (isBullishOB) {
      obs.push({
        type: 'bullish',
        high: candle.high || candle.close,
        low:  candle.low  || candle.close,
        open: candle.open,
        close: candle.close,
        date: candle.date,
        strength: Math.min(100, Math.round(displacement / candle.close * 1000))
      });
    } else if (isBearishOB) {
      obs.push({
        type: 'bearish',
        high: candle.high || candle.close,
        low:  candle.low  || candle.close,
        open: candle.open,
        close: candle.close,
        date: candle.date,
        strength: Math.min(100, Math.round(displacement / candle.close * 1000))
      });
    }
  }

  // Return most recent 3, filter to ones price hasn't broken through
  const currentPrice = c[c.length - 1].close;
  return obs
    .filter(ob => ob.type === 'bullish' ? currentPrice > ob.low : currentPrice < ob.high)
    .slice(-3)
    .reverse();
}

function detectFVGs(candles) {
  if (!candles || candles.length < 3) return [];
  const c = candles.slice(-60);
  const fvgs = [];

  for (let i = 1; i < c.length - 1; i++) {
    const prev = c[i - 1];
    const curr = c[i];
    const next = c[i + 1];

    const prevHigh = prev.high || prev.close;
    const nextLow  = next.low  || next.close;
    const prevLow  = prev.low  || prev.close;
    const nextHigh = next.high || next.close;

    // Bullish FVG: gap between prev high and next low (going up)
    if (nextLow > prevHigh) {
      fvgs.push({ type: 'bullish', top: nextLow, bottom: prevHigh, date: curr.date, filled: false });
    }
    // Bearish FVG: gap between next high and prev low (going down)
    if (nextHigh < prevLow) {
      fvgs.push({ type: 'bearish', top: prevLow, bottom: nextHigh, date: curr.date, filled: false });
    }
  }

  const currentPrice = c[c.length - 1].close;
  // Mark filled FVGs
  return fvgs
    .map(fvg => ({ ...fvg, filled: fvg.type === 'bullish' ? currentPrice < fvg.bottom : currentPrice > fvg.top }))
    .filter(fvg => !fvg.filled)
    .slice(-4)
    .reverse();
}

function detectLiquidity(candles) {
  if (!candles || candles.length < 10) return { bsl: [], ssl: [] };
  const c = candles.slice(-50);
  const highs = c.map(x => x.high || x.close);
  const lows  = c.map(x => x.low  || x.close);

  // Find equal highs (within 0.3%)
  const bsl = [], ssl = [];
  for (let i = 0; i < highs.length - 1; i++) {
    for (let j = i + 2; j < highs.length; j++) {
      const diff = Math.abs(highs[i] - highs[j]) / highs[i];
      if (diff < 0.003) {
        bsl.push({ price: (highs[i] + highs[j]) / 2, type: 'EQH', date: c[j].date });
        break;
      }
    }
  }
  for (let i = 0; i < lows.length - 1; i++) {
    for (let j = i + 2; j < lows.length; j++) {
      const diff = Math.abs(lows[i] - lows[j]) / lows[i];
      if (diff < 0.003) {
        ssl.push({ price: (lows[i] + lows[j]) / 2, type: 'EQL', date: c[j].date });
        break;
      }
    }
  }

  const currentPrice = c[c.length - 1].close;
  return {
    bsl: bsl.filter(x => x.price > currentPrice).slice(-2),
    ssl: ssl.filter(x => x.price < currentPrice).slice(-2),
    recentHigh: Math.max(...highs.slice(-20)),
    recentLow:  Math.min(...lows.slice(-20))
  };
}

// ── SMC Candlestick Chart ─────────────────────────────────────────────────
const SMCCandleChart = ({ candles, orderBlocks, fvgs, structure, liquidity }) => {
  const [tooltip, setTooltip] = React.useState(null);
  const [zoom, setZoom] = React.useState({ start: 0, end: 1 }); // 0-1 range of candles visible
  const [dragging, setDragging] = React.useState(null); // { startX, startZoom }
  const svgRef = React.useRef(null);
  const zoomRef = React.useRef(zoom);
  zoomRef.current = zoom;

  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.05 : -0.05;
      const prev = zoomRef.current;
      const range = prev.end - prev.start;
      const newRange = Math.max(0.1, Math.min(1, range + delta));
      const mid = (prev.start + prev.end) / 2;
      const newStart = Math.max(0, Math.min(1 - newRange, mid - newRange / 2));
      setZoom({ start: newStart, end: newStart + newRange });
    };
    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handleWheel);
  }, []);

  if (!candles || candles.length < 5) return null;

  const allCandles = candles.slice(-100); // pool of last 100
  const total = allCandles.length;
  const startIdx = Math.floor(zoom.start * total);
  const endIdx   = Math.max(startIdx + 5, Math.floor(zoom.end * total));
  const data = allCandles.slice(startIdx, endIdx);
  const W = 900, H = 420;
  const PAD = { top: 30, right: 80, bottom: 40, left: 65 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const allPrices = data.flatMap(d => [d.high, d.low]).filter(p => p != null && !isNaN(p) && p > 0);
  if (!allPrices.length) return null;
  const minP = Math.min(...allPrices) * 0.998;
  const maxP = Math.max(...allPrices) * 1.002;
  const priceRange = maxP - minP || 1;

  const toY = p => PAD.top + ((maxP - p) / priceRange) * chartH;
  const toX = i => PAD.left + (i / (data.length - 1)) * chartW;
  const candleW = Math.max(3, (chartW / data.length) * 0.6);

  // X-axis labels — show every ~10 candles
  const isIntraday = data.length > 0 && data[0].date && (data[0].date.includes('T') || data[0].date.includes(' ')) && data.length > 40;
  const xLabels = data.map((d, i) => ({
    i,
    label: d.date
      ? isIntraday
        ? (d.date.includes('T') ? d.date.slice(11, 16) : d.date.slice(11, 16))  // HH:MM from ISO or space-separated
        : d.date.slice(0, 10)    // show YYYY-MM-DD for daily
      : ''
  })).filter((_, i) => i % Math.ceil(data.length / 8) === 0);

  // Y-axis labels
  const yTicks = 6;
  const yLabels = Array.from({ length: yTicks }, (_, i) => {
    const price = minP + (priceRange / (yTicks - 1)) * i;
    return { price, y: toY(price) };
  });

  const currentPrice = data[data.length - 1].close;

  return (
    <div style={{ width: '100%', marginBottom: '1rem' }}>
      {/* Zoom controls */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
        <span style={{ fontSize: '0.7rem', color: '#475569' }}>Zoom:</span>
        {[
          { label: '1M',  start: 0.7,  end: 1 },
          { label: '3M',  start: 0.4,  end: 1 },
          { label: '6M',  start: 0.15, end: 1 },
          { label: 'All', start: 0,    end: 1 },
        ].map(z => (
          <button key={z.label} onClick={() => setZoom({ start: z.start, end: z.end })}
            style={{
              padding: '0.2rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer',
              background: zoom.start === z.start ? 'rgba(99,102,241,0.3)' : 'rgba(30,41,59,0.5)',
              border: zoom.start === z.start ? '1px solid #6366f1' : '1px solid #334155',
              borderRadius: '4px', color: zoom.start === z.start ? '#a5b4fc' : '#64748b'
            }}>{z.label}</button>
        ))}
        <span style={{ fontSize: '0.68rem', color: '#334155', marginLeft: 'auto' }}>
          Scroll to zoom · Drag to pan · Double-click to reset
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', minWidth: 500, height: 'auto', display: 'block', cursor: dragging ? 'grabbing' : 'crosshair' }}
        onMouseLeave={() => { setTooltip(null); setDragging(null); }}
        onDoubleClick={() => setZoom({ start: 0, end: 1 })}

        onMouseDown={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setDragging({ startX: e.clientX, startZoom: { ...zoom } });
        }}
        onMouseMove={(e) => {
          if (!dragging) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const dx = (e.clientX - dragging.startX) / rect.width;
          const range = dragging.startZoom.end - dragging.startZoom.start;
          const shift = -dx * range * 2;
          const newStart = Math.max(0, Math.min(1 - range, dragging.startZoom.start + shift));
          setZoom({ start: newStart, end: newStart + range });
        }}
        onMouseUp={() => setDragging(null)}
      >
        {/* Background */}
        <rect x={PAD.left} y={PAD.top} width={chartW} height={chartH} fill="rgba(15,23,42,0.8)" rx="4" />

        {/* Grid lines */}
        {yLabels.map((t, i) => (
          <line key={i} x1={PAD.left} y1={t.y} x2={PAD.left + chartW} y2={t.y}
            stroke="#1e293b" strokeWidth="1" strokeDasharray="4,4" />
        ))}

        {/* FVG zones */}
        {fvgs && fvgs.slice(0, 4).map((fvg, i) => {
          const y1 = toY(fvg.top), y2 = toY(fvg.bottom);
          if (y1 < PAD.top || y2 > PAD.top + chartH) return null;
          return (
            <g key={`fvg-${i}`}>
              <rect x={PAD.left} y={Math.min(y1,y2)} width={chartW}
                height={Math.abs(y2 - y1)}
                fill={fvg.type === 'bullish' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'}
                stroke={fvg.type === 'bullish' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}
                strokeWidth="0.5" strokeDasharray="3,3" />
              <text x={PAD.left + chartW + 3} y={Math.min(y1,y2) + Math.abs(y2-y1)/2 + 4}
                fill={fvg.type === 'bullish' ? '#22c55e' : '#ef4444'}
                fontSize="8" fontWeight="700">{fvg.type === 'bullish' ? 'BFVG' : 'BFVG'}</text>
            </g>
          );
        })}

        {/* OB zones */}
        {orderBlocks && orderBlocks.slice(0, 3).map((ob, i) => {
          const y1 = toY(ob.high), y2 = toY(ob.low);
          if (y1 < PAD.top - 10 || y2 > PAD.top + chartH + 10) return null;
          const clampY1 = Math.max(PAD.top, y1);
          const clampY2 = Math.min(PAD.top + chartH, y2);
          return (
            <g key={`ob-${i}`}>
              <rect x={PAD.left} y={clampY1} width={chartW}
                height={Math.max(2, clampY2 - clampY1)}
                fill={ob.type === 'bullish' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'}
                stroke={ob.type === 'bullish' ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'}
                strokeWidth="1" />
              <text x={PAD.left + 4} y={clampY1 + 10}
                fill={ob.type === 'bullish' ? '#22c55e' : '#ef4444'}
                fontSize="8" fontWeight="700">{ob.type === 'bullish' ? '▲OB' : '▼OB'}</text>
            </g>
          );
        })}

        {/* Liquidity lines */}
        {liquidity && liquidity.bsl && liquidity.bsl.map((l, i) => {
          const y = toY(l.price);
          if (y < PAD.top || y > PAD.top + chartH) return null;
          return (
            <g key={`bsl-${i}`}>
              <line x1={PAD.left} y1={y} x2={PAD.left + chartW} y2={y}
                stroke="#22c55e" strokeWidth="1" strokeDasharray="6,3" opacity="0.7" />
              <text x={PAD.left + chartW + 3} y={y + 4}
                fill="#22c55e" fontSize="8">BSL</text>
            </g>
          );
        })}
        {liquidity && liquidity.ssl && liquidity.ssl.map((l, i) => {
          const y = toY(l.price);
          if (y < PAD.top || y > PAD.top + chartH) return null;
          return (
            <g key={`ssl-${i}`}>
              <line x1={PAD.left} y1={y} x2={PAD.left + chartW} y2={y}
                stroke="#ef4444" strokeWidth="1" strokeDasharray="6,3" opacity="0.7" />
              <text x={PAD.left + chartW + 3} y={y + 4}
                fill="#ef4444" fontSize="8">SSL</text>
            </g>
          );
        })}

        {/* Equilibrium line */}
        {structure && structure.equilibrium && (() => {
          const y = toY(structure.equilibrium);
          return y > PAD.top && y < PAD.top + chartH ? (
            <line x1={PAD.left} y1={y} x2={PAD.left + chartW} y2={y}
              stroke="#64748b" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
          ) : null;
        })()}

        {/* Candles */}
        {data.map((d, i) => {
          const x = toX(i);
          const openY  = toY(d.open);
          const closeY = toY(d.close);
          const highY  = toY(d.high);
          const lowY   = toY(d.low);
          const bullish = d.close >= d.open;
          const color   = bullish ? '#22c55e' : '#ef4444';
          const bodyTop = Math.min(openY, closeY);
          const bodyH   = Math.max(1, Math.abs(closeY - openY));

          return (
            <g key={i} onMouseEnter={() => setTooltip({ d, x, y: bodyTop })}>
              {/* Wick */}
              <line x1={x} y1={highY} x2={x} y2={lowY} stroke={color} strokeWidth="1" opacity="0.8" />
              {/* Body */}
              <rect x={x - candleW/2} y={bodyTop} width={candleW} height={bodyH}
                fill={bullish ? color : color} stroke={color} strokeWidth="0.5"
                opacity={bullish ? 0.85 : 0.9} />
            </g>
          );
        })}

        {/* BOS / CHoCH labels */}
        {structure && structure.bos && (() => {
          const y = toY(structure.bos.price);
          return y > PAD.top && y < PAD.top + chartH ? (
            <g>
              <line x1={PAD.left} y1={y} x2={PAD.left + chartW * 0.7} y2={y}
                stroke="#60a5fa" strokeWidth="1.5" strokeDasharray="5,3" />
              <rect x={PAD.left + 4} y={y - 10} width={42} height={14} fill="rgba(96,165,250,0.2)" rx="3" />
              <text x={PAD.left + 8} y={y + 2} fill="#60a5fa" fontSize="9" fontWeight="800">BOS ↑</text>
            </g>
          ) : null;
        })()}
        {structure && structure.choch && (() => {
          const y = toY(structure.choch.price);
          return y > PAD.top && y < PAD.top + chartH ? (
            <g>
              <line x1={PAD.left} y1={y} x2={PAD.left + chartW * 0.5} y2={y}
                stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="5,3" />
              <rect x={PAD.left + 4} y={y - 10} width={52} height={14} fill="rgba(245,158,11,0.2)" rx="3" />
              <text x={PAD.left + 8} y={y + 2} fill="#f59e0b" fontSize="9" fontWeight="800">CHoCH ⚠</text>
            </g>
          ) : null;
        })()}

        {/* Current price line */}
        {(() => {
          const y = toY(currentPrice);
          return (
            <g>
              <line x1={PAD.left} y1={y} x2={PAD.left + chartW} y2={y}
                stroke="white" strokeWidth="0.5" opacity="0.3" />
              <rect x={PAD.left + chartW + 1} y={y - 8} width={58} height={16} fill="#334155" rx="3" />
              <text x={PAD.left + chartW + 4} y={y + 4} fill="white" fontSize="10" fontWeight="700">
                ${currentPrice.toFixed(2)}
              </text>
            </g>
          );
        })()}

        {/* Y-axis labels */}
        {yLabels.map((t, i) => (
          <text key={i} x={PAD.left - 4} y={t.y + 4} textAnchor="end"
            fill="#64748b" fontSize="9">${t.price.toFixed(0)}</text>
        ))}

        {/* X-axis labels */}
        {xLabels.map(({ i, label }) => (
          <text key={i} x={toX(i)} y={PAD.top + chartH + 20} textAnchor="middle"
            fill="#64748b" fontSize="8">{isIntraday ? label : label.slice(5)}</text>
        ))}

        {/* Tooltip */}
        {tooltip && (() => {
          const { d, x, y } = tooltip;
          const tw = 110, th = 80;
          const tx = x + tw > W - PAD.right ? x - tw - 8 : x + 8;
          const ty = Math.max(PAD.top, Math.min(y, PAD.top + chartH - th));
          return (
            <g>
              <rect x={tx} y={ty} width={tw} height={th} fill="rgba(15,23,42,0.95)"
                stroke="#334155" strokeWidth="1" rx="6" />
              <text x={tx+8} y={ty+14} fill="#94a3b8" fontSize="9">{isIntraday ? d.date?.slice(0,16).replace('T',' ') : d.date?.slice(0,10)}</text>
              {[['O', d.open], ['H', d.high], ['L', d.low], ['C', d.close]].map(([l, v], i) => (
                <text key={l} x={tx+8} y={ty+26+i*13} fill={l==='C' ? 'white' : '#cbd5e1'} fontSize="10">
                  <tspan fill="#64748b">{l}: </tspan>${parseFloat(v).toFixed(2)}
                </text>
              ))}
            </g>
          );
        })()}

        {/* Legend */}
        {[
          { color: 'rgba(34,197,94,0.3)', label: 'Bullish OB/FVG', x: PAD.left },
          { color: 'rgba(239,68,68,0.3)',  label: 'Bearish OB/FVG', x: PAD.left + 110 },
          { color: '#22c55e', label: 'BSL', x: PAD.left + 220, dash: true },
          { color: '#ef4444', label: 'SSL', x: PAD.left + 270, dash: true },
          { color: '#60a5fa', label: 'BOS', x: PAD.left + 320, dash: true },
        ].map((l, i) => (
          <g key={i}>
            <rect x={l.x} y={6} width={12} height={12} fill={l.color} rx="2" />
            <text x={l.x + 15} y={16} fill="#64748b" fontSize="9">{l.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// ── SmartMoneyTab Component ──────────────────────────────────────────────
const SmartMoneyTab = ({ symbol, priceData, priceLoading, onFetchPrice }) => {
  const API_URL_SMC = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';
  const [smcTimeframe, setSmcTimeframe] = React.useState('daily');
  const [smcData, setSmcData] = React.useState(null);
  const [smcLoading, setSmcLoading] = React.useState(false);

  // Clear smcData when symbol changes so stale candles never show
  React.useEffect(() => {
    setSmcData(null);
  }, [symbol]);

  // Fetch SMC-specific data when timeframe, symbol, or priceData changes
  React.useEffect(() => {
    if (!symbol) return;
    if (smcTimeframe === 'daily') {
      if (priceData) { setSmcData(priceData); return; }
      return; // wait for priceData to arrive
    }
    const fetchSMC = async () => {
      setSmcLoading(true);
      try {
        const intervalMap = { '60min': 'intraday?interval=60min', '15min': 'intraday?interval=15min', '5min': 'intraday?interval=5min' };
        const tf = intervalMap[smcTimeframe] || 'daily';
        const res = await fetch(`${API_URL_SMC}/stocks/${symbol}/prices/${tf}`);
        const data = await res.json();
        setSmcData(data);
      } catch (e) { console.error('SMC fetch error:', e); }
      setSmcLoading(false);
    };
    fetchSMC();
  }, [smcTimeframe, symbol, priceData]);

  // Use smcData if available, fall back to priceData
  const activeData = smcData || priceData;
  const activeLoading = smcLoading || (smcTimeframe === 'daily' && priceLoading);

  const candles = React.useMemo(() => {
    if (!activeData) return null;
    const raw = activeData.data || activeData.prices || activeData;
    if (!Array.isArray(raw)) return null;
    return raw.map(d => {
      // Preserve full datetime for intraday (e.g. "2026-03-31 09:30:00")
      const rawDate = d.date || d.timestamp || '';
      const dateStr = rawDate instanceof Date ? rawDate.toISOString() : String(rawDate);
      return {
        date:  dateStr,
        open:  parseFloat(d.open  || d.close || 0),
        high:  parseFloat(d.high  || d.close || 0),
        low:   parseFloat(d.low   || d.close || 0),
        close: parseFloat(d.close || 0),
      };
    }).filter(d => d.close > 0);
  }, [activeData]);

  const structure  = React.useMemo(() => candles ? detectMarketStructure(candles) : null, [candles]);
  const orderBlocks = React.useMemo(() => candles ? detectOrderBlocks(candles) : [], [candles]);
  const fvgs       = React.useMemo(() => candles ? detectFVGs(candles) : [], [candles]);
  const liquidity  = React.useMemo(() => candles ? detectLiquidity(candles) : null, [candles]);
  const currentPrice = candles ? candles[candles.length - 1]?.close : null;

  const bias = React.useMemo(() => {
    if (!structure || !currentPrice) return null;
    const { trend, zone } = structure;
    if (trend === 'bullish' && zone === 'discount') return { label: 'BULLISH', color: '#22c55e', sub: 'Price in discount — look for buys at OB/FVG' };
    if (trend === 'bullish' && zone === 'premium')  return { label: 'NEUTRAL', color: '#f59e0b', sub: 'Bullish trend but price in premium — wait for pullback' };
    if (trend === 'bearish' && zone === 'premium')  return { label: 'BEARISH', color: '#ef4444', sub: 'Price in premium — look for sells at OB/FVG' };
    if (trend === 'bearish' && zone === 'discount') return { label: 'NEUTRAL', color: '#f59e0b', sub: 'Bearish trend but price in discount — wait for bounce' };
    return { label: 'RANGING', color: '#94a3b8', sub: 'No clear directional bias — avoid trading' };
  }, [structure, currentPrice]);

  const cardStyle = { background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem', marginBottom: '1rem' };
  const headerStyle = { fontSize: '0.8rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' };
  const fmtPrice = (p) => p ? `$${parseFloat(p).toFixed(2)}` : '—';

  if (activeLoading) return (
    <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚙️</div>
      <div>Analyzing price data...</div>
    </div>
  );

  if (!activeData || !candles) return (
    <div style={{ padding: '3rem', textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📊</div>
      <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>Load price data to run Smart Money analysis</p>
      <button onClick={() => smcTimeframe === 'daily' ? onFetchPrice() : setSmcTimeframe('daily')} style={{ padding: '0.75rem 2rem', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', fontWeight: '600' }}>
        Load Price Data
      </button>
    </div>
  );

  return (
    <div style={{ padding: '1.5rem 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>🏦 Smart Money Analysis</h2>
          <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{symbol} · {smcTimeframe === 'daily' ? 'Daily OHLC' : smcTimeframe + ' Intraday'} · {candles ? candles.length : 0} candles</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Timeframe selector */}
          <div style={{ display: 'flex', gap: '0.25rem', background: 'rgba(15,23,42,0.5)', padding: '0.2rem', borderRadius: '8px', border: '1px solid #334155' }}>
            {[
              { label: 'Daily', value: 'daily', sub: 'EOD' },
              { label: '1H', value: '60min', sub: 'Intraday' },
              { label: '15M', value: '15min', sub: 'Intraday' },
              { label: '5M', value: '5min', sub: 'Intraday' },
            ].map(tf => (
              <button key={tf.value} onClick={() => setSmcTimeframe(tf.value)}
                title={tf.sub}
                style={{
                  padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: '700',
                  cursor: 'pointer', borderRadius: '6px', border: 'none',
                  background: smcTimeframe === tf.value ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : 'transparent',
                  color: smcTimeframe === tf.value ? 'white' : '#64748b',
                  transition: 'all 0.15s'
                }}>{tf.label}</button>
            ))}
          </div>
          {smcLoading && <span style={{ fontSize: '0.72rem', color: '#7c3aed' }}>⚙️ Loading...</span>}
          <span style={{ fontSize: '0.72rem', color: '#475569', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', padding: '0.3rem 0.6rem', borderRadius: '6px' }}>
            📚 Learn SMC → Module 43–47
          </span>
          <button onClick={() => smcTimeframe === 'daily' ? onFetchPrice() : setSmcTimeframe(smcTimeframe)}
            style={{ padding: '0.4rem 0.75rem', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#94a3b8', cursor: 'pointer', fontSize: '0.78rem' }}>↻ Refresh</button>
        </div>
      </div>

      {/* ── Candlestick Chart with SMC overlays ── */}
      <div style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
          <span>📊 Candlestick Chart with SMC Overlays — hover candles for OHLC</span>
          <span style={{ color: '#475569' }}>{candles ? candles.length : 0} candles</span>
        </div>
        <SMCCandleChart
          candles={candles}
          orderBlocks={orderBlocks}
          fvgs={fvgs}
          structure={structure}
          liquidity={liquidity}
        />
      </div>

      {/* ── Actionable Signal Banner ── */}
      {(() => {
        const rangeHigh = structure?.rangeHigh || 0;
        const rangeLow  = structure?.rangeLow  || 0;
        const equil     = structure?.equilibrium || 0;
        const zonePos   = structure?.zonePercent ? parseInt(structure.zonePercent) : null;
        const signalColor = bias ? bias.color : '#94a3b8';

        // Generate actionable watch list from live data
        const watchItems = [];
        // Nearest bearish OB
        const nearOB = orderBlocks.filter(o => o.type === 'bearish').sort((a,b) => Math.abs(((currentPrice-(a.high+a.low)/2)/currentPrice)) - Math.abs(((currentPrice-(b.high+b.low)/2)/currentPrice)))[0];
        if (nearOB) {
          const pct = (Math.abs(((currentPrice - (nearOB.high+nearOB.low)/2)/currentPrice)*100)).toFixed(1);
          watchItems.push({ color: '#ef4444', title: `Resistance at $${nearOB.low.toFixed(2)}–$${nearOB.high.toFixed(2)} bearish OB`, body: `${pct}% away. If price taps and rejects this zone, it signals short-side pressure.` });
        }
        // Nearest bullish FVG
        const bullFVG = fvgs.filter(f => f.type === 'bullish').sort((a,b) => Math.abs(currentPrice-(a.top+a.bottom)/2) - Math.abs(currentPrice-(b.top+b.bottom)/2))[0];
        if (bullFVG) {
          const pct = (Math.abs(((currentPrice-(bullFVG.top+bullFVG.bottom)/2)/currentPrice)*100)).toFixed(1);
          watchItems.push({ color: '#22c55e', title: `Long opportunity at $${bullFVG.bottom.toFixed(2)}–$${bullFVG.top.toFixed(2)} bullish FVG`, body: `${pct}% away. A pullback into this gap near equilibrium is the discount buy zone.` });
        }
        // BSL target
        const bslTarget = liquidity?.bsl?.[0];
        if (bslTarget) {
          const pct = (((bslTarget.price - currentPrice)/currentPrice)*100).toFixed(1);
          watchItems.push({ color: '#60a5fa', title: `Liquidity target above at $${bslTarget.price.toFixed(2)}`, body: `+${pct}% from current. Equal highs here attract price — potential next target if bulls take control.` });
        }
        // Zone warning
        if (zonePos !== null) {
          if (zonePos > 70) watchItems.push({ color: '#f59e0b', title: 'Avoid new longs at current price', body: `Price is at ${zonePos}% of its range — deep in premium. Smart money distributes here, not accumulates.` });
          else if (zonePos < 30) watchItems.push({ color: '#22c55e', title: 'Discount zone — favorable for longs', body: `Price is at ${zonePos}% of its range — in discount. This is where smart money typically accumulates positions.` });
        }

        return (
          <>
            {/* Signal banner */}
            <div style={{ background: 'rgba(30,41,59,0.6)', border: `1px solid ${signalColor}33`, borderRadius: '0.75rem', padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: signalColor, flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: '0.72rem', color: '#64748b', margin: 0 }}>Current signal · {symbol} · {smcTimeframe === 'daily' ? 'Daily' : smcTimeframe + ' Intraday'}</p>
                    <p style={{ fontSize: '1.3rem', fontWeight: '700', margin: 0, color: signalColor }}>{bias ? bias.label : 'NO DATA'}</p>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0.1rem 0 0' }}>{bias ? bias.sub : ''}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Current price', value: currentPrice ? `$${currentPrice.toFixed(2)}` : '—' },
                    { label: 'Position in range', value: zonePos !== null ? `${zonePos}%` : '—' },
                    { label: 'Zone', value: structure?.zone?.toUpperCase() || '—', color: structure?.zone === 'discount' ? '#22c55e' : '#ef4444' },
                  ].map(m => (
                    <div key={m.label} style={{ textAlign: 'center', padding: '0.5rem 0.875rem', background: 'rgba(15,23,42,0.5)', border: '1px solid #334155', borderRadius: '0.5rem' }}>
                      <p style={{ fontSize: '0.65rem', color: '#64748b', margin: 0 }}>{m.label}</p>
                      <p style={{ fontSize: '1rem', fontWeight: '700', margin: 0, color: m.color || '#e2e8f0' }}>{m.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Range bar */}
              {structure && zonePos !== null && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#475569', marginBottom: '0.3rem' }}>
                    <span>Low ${rangeLow.toFixed(2)}</span>
                    <span>Equilibrium ${equil.toFixed(2)}</span>
                    <span>High ${rangeHigh.toFixed(2)}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, position: 'relative' }}>
                    <div style={{ position: 'absolute', left: '50%', top: -2, width: 1, height: 10, background: '#475569', opacity: 0.5 }} />
                    <div style={{ width: `${Math.min(100, zonePos)}%`, height: '100%', background: signalColor, borderRadius: 3, opacity: 0.7 }} />
                    <div style={{ position: 'absolute', left: `${Math.min(100, zonePos)}%`, top: -4, width: 14, height: 14, borderRadius: '50%', background: signalColor, border: '2px solid #0f172a', transform: 'translateX(-50%)' }} />
                  </div>
                  <p style={{ fontSize: '0.68rem', color: '#64748b', margin: '0.3rem 0 0', textAlign: 'center' }}>
                    {zonePos > 70 ? 'Price in premium — high risk for new longs' : zonePos < 30 ? 'Price in discount — favorable for longs' : 'Price near equilibrium — wait for direction'}
                  </p>
                </div>
              )}
            </div>

            {/* What to watch */}
            {watchItems.length > 0 && (
              <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.75rem' }}>👁 What to watch for</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {watchItems.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.6rem 0.75rem', background: 'rgba(15,23,42,0.4)', borderRadius: '0.5rem' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: item.color, marginTop: 5, flexShrink: 0 }} />
                      <div>
                        <p style={{ fontSize: '0.8rem', fontWeight: '700', color: '#e2e8f0', margin: 0 }}>{item.title}</p>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.15rem 0 0' }}>{item.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key levels grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              {/* Resistance */}
              <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.6rem' }}>Resistance above</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {orderBlocks.filter(o => o.type === 'bearish').slice(0,3).map((ob, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color: '#64748b' }}>Bearish OB</span>
                      <span style={{ color: '#ef4444', fontWeight: '600' }}>${ob.low.toFixed(2)}–${ob.high.toFixed(2)}</span>
                    </div>
                  ))}
                  {liquidity?.bsl?.map((l, i) => (
                    <div key={`bsl-${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color: '#64748b' }}>BSL target ({l.type})</span>
                      <span style={{ color: '#60a5fa', fontWeight: '600' }}>${l.price.toFixed(2)}</span>
                    </div>
                  ))}
                  {liquidity?.recentHigh && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0' }}>
                      <span style={{ color: '#64748b' }}>Recent high</span>
                      <span style={{ color: '#60a5fa', fontWeight: '600' }}>${liquidity.recentHigh.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Support */}
              <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.6rem' }}>Support below</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {fvgs.filter(f => f.type === 'bullish').slice(0,3).map((fvg, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color: '#64748b' }}>Bullish FVG</span>
                      <span style={{ color: '#22c55e', fontWeight: '600' }}>${fvg.bottom.toFixed(2)}–${fvg.top.toFixed(2)}</span>
                    </div>
                  ))}
                  {equil > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color: '#64748b' }}>Equilibrium</span>
                      <span style={{ color: '#f59e0b', fontWeight: '600' }}>${equil.toFixed(2)}</span>
                    </div>
                  )}
                  {liquidity?.ssl?.map((l, i) => (
                    <div key={`ssl-${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color: '#64748b' }}>SSL target ({l.type})</span>
                      <span style={{ color: '#60a5fa', fontWeight: '600' }}>${l.price.toFixed(2)}</span>
                    </div>
                  ))}
                  {liquidity?.recentLow && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0' }}>
                      <span style={{ color: '#64748b' }}>Recent low</span>
                      <span style={{ color: '#60a5fa', fontWeight: '600' }}>${liquidity.recentLow.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Market structure detail */}
              {structure && (
                <div style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.25rem' }}>
                  <p style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.6rem' }}>Market structure</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {structure.bos && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <span style={{ color: '#64748b' }}>BOS</span>
                        <span style={{ color: structure.bos.direction === 'bullish' ? '#22c55e' : '#ef4444', fontWeight: '600' }}>{structure.bos.label}</span>
                      </div>
                    )}
                    {structure.choch && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <span style={{ color: '#64748b' }}>CHoCH</span>
                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>{structure.choch.label}</span>
                      </div>
                    )}
                    {!structure.bos && !structure.choch && (
                      <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>No clear BOS or CHoCH detected</p>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color: '#64748b' }}>Range high</span>
                      <span style={{ color: '#22c55e', fontWeight: '600' }}>${rangeHigh.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color: '#64748b' }}>Equilibrium</span>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>${equil.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '0.35rem 0' }}>
                      <span style={{ color: '#64748b' }}>Range low</span>
                      <span style={{ color: '#ef4444', fontWeight: '600' }}>${rangeLow.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Disclaimer */}
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(15,23,42,0.5)', borderRadius: '0.5rem', border: '1px solid #1e293b' }}>
              <p style={{ fontSize: '0.7rem', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                ⚠️ <strong style={{ color: '#64748b' }}>Educational purposes only.</strong> Smart Money Concepts analysis is based on algorithmic detection of price patterns and is not financial advice. InvestorQ is not a registered investment advisor. Always conduct your own research before making any investment decisions. Learn more in <strong style={{ color: '#7c3aed' }}>Learn Academy → SMC Masterclass (Modules 43–47)</strong>.
              </p>
            </div>
          </>
        );
      })()}

    </div>
  );
};

export default CompanyAnalysis;